import React, { useEffect, useState } from 'react';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { ResetCurrentDynamic as BLResetCurrentDynamic } from '../../../utilities/BL/Zones';
import { QRCodeCanvas } from 'qrcode.react'; // IMPORTANTE PARA EL QR
import { 
    StartTacoGame, 
    ResetTacoGame, 
    ConnectWebSocketForTacoAdmin, 
    DisconnectWebSocketForTacoAdmin, 
    ShowTacoFrontPageBL,
    EmergencyKickTacoPlayers
} from '../../../utilities/BL/Tacos';

const Tacos = ({ getOutOfTheDynamic }) => {
    // ==========================================================================
    // ESTADOS DE CONFIGURACIÓN
    // ==========================================================================
    const [durationSeconds, setDurationSeconds] = useState(20);
    const [winMode, setWinMode] = useState(0); // 0: Tiempo, 1: Muerte Súbita (Llegar a meta)
    const [targetTacosToWin, setTargetTacosToWin] = useState(20);

    // ==========================================================================
    // ESTADOS DE LA PARTIDA Y RED
    // ==========================================================================
    const [matchPhase, setMatchPhase] = useState('WAITING');
    const [isWorking, setIsWorking] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [wsStatus, setWsStatus] = useState('idle');

    // Estadísticas en vivo
    const [player1Stats, setPlayer1Stats] = useState({ tacosCompleted: 0, currentStep: 1 });
    const [player2Stats, setPlayer2Stats] = useState({ tacosCompleted: 0, currentStep: 1 });
    const [matchWinner, setMatchWinner] = useState('none');
    
    // Reloj local visual
    const [localTimeLeft, setLocalTimeLeft] = useState(0);

    // NUEVO: ESTADOS PARA EL MODAL DEL QR
    const [showQRModal, setShowQRModal] = useState(false);
    const playerUrl = `${window.location.origin}/tacos`; // Ruta para los jugadores

    // ==========================================================================
    // WEBSOCKET
    // ==========================================================================
    useEffect(() => {
        ConnectWebSocketForTacoAdmin(handleIncomingMessage, setWsStatus);
        return () => DisconnectWebSocketForTacoAdmin();
    }, []);

    const handleIncomingMessage = (msg) => {
        const type = msg.type.toLowerCase();
        const payload = msg.payload || {};

        if (type === 'taco.progress' || type === 'taco.finished') {
            setMatchPhase(payload.status === 1 ? 'RUNNING' : payload.status === 2 ? 'FINISHED' : 'WAITING');
            setMatchWinner(payload.winner || 'none');
            
            if (payload.player1) setPlayer1Stats(payload.player1);
            if (payload.player2) setPlayer2Stats(payload.player2);
            
            // Sincronizar reloj si acaba de iniciar
            if (type === 'taco.progress' && payload.status === 1 && payload.durationSeconds) {
                // Solo inicializamos el reloj local si no estaba corriendo (evita saltos)
                setLocalTimeLeft(prev => prev === 0 ? payload.durationSeconds : prev);
            }
        }
    };

    // Temporizador visual local para el Admin
    useEffect(() => {
        let timer;
        if (matchPhase === 'RUNNING' && localTimeLeft > 0 && winMode === 0) {
            timer = setInterval(() => {
                setLocalTimeLeft(prev => Math.max(0, prev - 1));
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [matchPhase, localTimeLeft, winMode]);

    // ==========================================================================
    // ACCIONES
    // ==========================================================================
    const handleStartGame = async () => {
        setIsWorking(true);
        setErrorMessage('');
        setLocalTimeLeft(durationSeconds); // Reseteamos reloj visual

        const config = {
            durationSeconds: durationSeconds,
            tapsPerStep: 1,     // Fijo: 1 tap = 1 paso
            stepsPerTaco: 4,    // Fijo: 4 pasos = 1 taco
            tacosPerPlate: 5,   // Fijo: 5 tacos = 1 plato
            winMode: winMode,
            targetTacosToWin: targetTacosToWin
        };

        const res = await StartTacoGame(config);
        if (!res.success) {
            setErrorMessage(res.message);
        }
        setIsWorking(false);
    };

    const handleResetGame = async () => {
        setIsWorking(true);
        setErrorMessage('');
        await ResetTacoGame();
        setMatchPhase('WAITING');
        setPlayer1Stats({ tacosCompleted: 0, currentStep: 1 });
        setPlayer2Stats({ tacosCompleted: 0, currentStep: 1 });
        setMatchWinner('none');
        setLocalTimeLeft(0);
        setIsWorking(false);
    };

    const handleShowFrontPage = async () => {
        setIsWorking(true);
        setErrorMessage('');
        const res = await ShowTacoFrontPageBL();
        if (!res.success) setErrorMessage(res.message);
        setIsWorking(false);
    };

    // MANEJADOR DE EMERGENCIA
    const handleEmergencyDisconnect = async () => {
        // Confirmación de seguridad para evitar clics por accidente
        if (!window.confirm("🚨 ¿ESTÁS SEGUR@? Esto expulsará a los jugadores actuales de sus celulares de inmediato.")) {
            return;
        }

        setIsWorking(true);
        setErrorMessage('');
        
        const res = await EmergencyKickTacoPlayers();
        if (!res.success) {
            setErrorMessage(res.message);
        } else {
            // Si la expulsión fue exitosa, también limpiamos la pantalla gigante
            await ResetTacoGame(); 
            setMatchPhase('WAITING');
            setPlayer1Stats({ tacosCompleted: 0, currentStep: 1 });
            setPlayer2Stats({ tacosCompleted: 0, currentStep: 1 });
            setMatchWinner('none');
            setLocalTimeLeft(0);
        }
        setIsWorking(false);
    };

    const handleReturnToDashboard = async () => {
        await BLResetCurrentDynamic();
        getOutOfTheDynamic();
    };

    // ==========================================================================
    // HELPERS VISUALES
    // ==========================================================================
    const translateWinner = (winnerStr) => {
        if (winnerStr === 'none') return '';
        if (winnerStr === 'tie') return '¡Empate!';

        return winnerStr === 'player1' ? '¡Ganó Juanjolote Local!' : '¡Ganó Juanjolote Visita!';
    };

    return (
        <SecondaryBase actionToExecute={handleReturnToDashboard}>
            <section className='AdmRunToGoal__Settings pb-5'>
                <p className='AdmRunToGoal__Title'>🌮 Velocidad taquera</p>

                <div className='AdmRunToGoal__Settings__Controls'>
                    
                    {/* ESTATUS Y RELOJ */}
                    <div className='AdmRunToGoal__Text--Center border-bottom pb-3 mb-4'>
                        <div className="d-flex justify-content-between align-items-center px-3">
                            <div>
                                <p className="mb-1 text-muted small">Estado de Red</p>
                                <p className="fw-bold mb-0">
                                    <span className={`badge ${wsStatus === 'connected' ? 'bg-success' : 'bg-warning'}`}>
                                        {wsStatus === 'connected' ? 'Conectado al Servidor' : wsStatus}
                                    </span>
                                </p>
                            </div>

                            <div className="bg-dark text-white rounded px-4 py-2 shadow-sm text-center">
                                <p className="mb-0 small text-uppercase" style={{ letterSpacing: '1px', color: '#aaa' }}>
                                    {matchPhase === 'WAITING' ? 'En Espera' : matchPhase === 'RUNNING' ? '¡Cocinando!' : 'Terminado'}
                                </p>
                                {winMode === 0 && matchPhase === 'RUNNING' && (
                                    <h3 className="mb-0 fw-bold text-warning" style={{ fontFamily: 'monospace' }}>
                                        {localTimeLeft}s
                                    </h3>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* CONFIGURACIÓN (Solo en espera) */}
                    {matchPhase === 'WAITING' && (
                        <div className="mb-4 bg-light p-3 rounded border">
                            <p className="fw-bold mb-3 border-bottom pb-2">Reglas de la partida</p>
                            
                            <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold'>Modo de Victoria:</label>
                            <select 
                                className='form-select mb-3'
                                value={winMode}
                                onChange={e => setWinMode(Number(e.target.value))}
                                disabled={isWorking}
                            >
                                <option value={0}>Por Tiempo (El que haga más tacos en X tiempo)</option>
                                {/* <option value={1}>Muerte Súbita / Desempate (El primero en llegar a X tacos)</option> */}
                            </select>

                            {winMode === 0 ? (
                                <>
                                    <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold'>Duración (Segundos):</label>
                                    <input 
                                        className='form-control mb-3' 
                                        type='number' 
                                        min={5} 
                                        value={durationSeconds} 
                                        onChange={e => setDurationSeconds(Number(e.target.value))} 
                                        disabled={isWorking}
                                    />
                                </>
                            ) : (
                                <>
                                    <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold text-danger'>Tacos necesarios para ganar automáticamente:</label>
                                    <input 
                                        className='form-control mb-3' 
                                        type='number' 
                                        min={1} 
                                        value={targetTacosToWin} 
                                        onChange={e => setTargetTacosToWin(Number(e.target.value))} 
                                        disabled={isWorking}
                                    />
                                </>
                            )}

                            <div className="d-flex gap-2 mt-4">
                                {/* BOTÓN DE PANTALLA */}
                                <button className={`btn btn-info fw-bold shadow-sm w-100 ${isWorking ? 'disabled' : ''}`} onClick={handleShowFrontPage} disabled={isWorking}>
                                    <i className="bi bi-display mb-1 fs-5"></i> Mostrar portada
                                </button>
                            </div>

                            <button 
                                className={`btn btn-success fw-bold w-100 mt-3 shadow-sm ${isWorking ? 'disabled' : ''}`}
                                onClick={handleStartGame}
                                disabled={isWorking}
                            >
                                <i className="bi bi-play-fill"></i> Iniciar dinámica
                            </button>
                        </div>
                    )}

                    {/* MONITOREO EN VIVO (Cuando está corriendo o terminó) */}
                    {matchPhase !== 'WAITING' && (
                        <div className="mb-4">
                            <h5 className="fw-bold mb-3 border-bottom pb-2">Marcador en Vivo</h5>
                            
                            <div className="row g-3 mb-4">
                                <div className="col-6">
                                    <div className={`p-3 rounded text-center border ${matchWinner === 'player1' ? 'border-success bg-success text-white' : 'bg-light'}`}>
                                        <p className="fw-bold text-uppercase">Juanjolote Local</p>
                                        <p className="fw-black mb-0">{player1Stats.tacosCompleted} <span className="fs-6">Tacos</span></p>
                                        <small>Armando ingrediente {player1Stats.currentStep}/4</small>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className={`p-3 rounded text-center border ${matchWinner === 'player2' ? 'border-success bg-success text-white' : 'bg-light'}`}>
                                        <p className="fw-bold text-uppercase">Juanjolote Visita</p>
                                        <p className="fw-black mb-0">{player2Stats.tacosCompleted} <span className="fs-6">Tacos</span></p>
                                        <small>Armando ingrediente {player2Stats.currentStep}/4</small>
                                    </div>
                                </div>
                            </div>

                            {matchPhase === 'FINISHED' && (
                                <div className="alert alert-warning text-center fw-bold fs-4 shadow-sm mb-4">
                                    <i className="bi bi-trophy-fill text-danger me-2"></i> 
                                    {translateWinner(matchWinner)}
                                </div>
                            )}

                            <button 
                                className={`btn btn-danger fw-bold w-100 shadow-sm ${isWorking ? 'disabled' : ''}`}
                                onClick={handleResetGame}
                                disabled={isWorking}
                            >
                                <i className="bi bi-arrow-counterclockwise"></i> Reiniciar y volver a configuración
                            </button>
                        </div>
                    )}
                    
                    <div className="mt-5 border-top pt-4">
                        <p>Instrucciones para abrir los controles de los jugadores</p>
                        {/* BOTÓN DE CÓDIGO QR */}
                        <button className="btn btn-outline-primary fw-bold shadow-sm w-100" onClick={() => setShowQRModal(true)} disabled={isWorking}>
                            <i className="bi bi-qr-code-scan mb-1 fs-5"></i> Instrucciones de conexión
                        </button>
                    </div>

                    {/* BOTÓN DE PÁNICO (Siempre visible) */}
                    <div className="mt-5 border-top pt-4">
                        <p className='text-center'>¿Deseas desconectar a todos para liberar a los personajes?</p>
                        <button 
                            className={`btn btn-outline-danger fw-bold w-100 ${isWorking ? 'disabled' : ''}`}
                            onClick={handleEmergencyDisconnect}
                            disabled={isWorking}
                            title="Desconecta a los aficionados que estén en la dinámica para liberar los personajes"
                        >
                            <i className="bi bi-x-octagon-fill me-2"></i> Desconectar a todos
                        </button>
                    </div>

                    {errorMessage && <p className='text-danger fw-bold mt-3 text-center'>{errorMessage}</p>}
                </div>
            </section>

            {/* =======================================================
                MODAL DEL CÓDIGO QR PARA CONECTAR JUGADORES
            ======================================================= */}
            {showQRModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                            <div className="modal-header bg-dark text-white border-0" style={{ borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}>
                                <p className="modal-title fw-bold"><i className="bi bi-phone-vibrate me-2"></i> Conectar Jugadores</p>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowQRModal(false)}></button>
                            </div>
                            <div className="modal-body text-center p-4">
                                <p className="text-muted mb-4">Pide a los participantes que escaneen este código para participar en la dinámica.</p>

                                <div className="bg-white p-3 d-inline-block rounded shadow-sm mb-4">
                                    <QRCodeCanvas value={playerUrl} size={200} level={"H"} />
                                </div>

                                <div className="input-group mb-3">
                                    <input type="text" className="form-control text-center text-muted bg-light" value={playerUrl} readOnly />
                                    <button className="btn btn-outline-secondary" onClick={() => { navigator.clipboard.writeText(playerUrl); alert('Enlace copiado al portapapeles'); }}>
                                        <i className="bi bi-copy"></i>
                                    </button>
                                </div>

                                <a href={playerUrl} target="_blank" rel="noreferrer" className="btn btn-primary fw-bold w-100 py-3 rounded shadow-sm mt-2">
                                    <i className="bi bi-box-arrow-up-right me-2"></i> Abrir juego en esta computadora
                                </a>

                                {/* BOTÓN DE EXPULSAR DIRECTO EN EL MODAL */}
                                <div className="mt-5 pt-3 border-top">
                                    <p className="text-muted small mb-2">¿Alguien se quedó conectado de la partida anterior?</p>
                                    <button 
                                        className="btn btn-outline-danger fw-bold w-100" 
                                        onClick={() => { handleEmergencyDisconnect(); setShowQRModal(false); }}
                                    >
                                        <i className="bi bi-x-octagon-fill me-2"></i> Expulsar jugadores atascados
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </SecondaryBase>
    );
};

export { Tacos };